import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { formatPhoneNumber } from '../../../shared/utils/format.utils';
import { SupportTicketService } from '../../../core/services/support-ticket.service';
import { ServiceAppointmentService } from '../../../core/services/service-appointment.service';
import { OrganizationService } from '../../../core/services/organization.service';
import {
  SupportTicket,
  SupportTicketStatus,
  SUPPORT_TICKET_STATUSES
} from '../../../core/models/support-ticket.model';
import {
  ServiceAppointment,
  ServiceAppointmentStatus,
  SERVICE_APPOINTMENT_STATUSES
} from '../../../core/models/service-appointment.model';
import {
  OrganizationActivity,
  EventType,
  getActivityIcon,
  getResourceRoute
} from '../../../core/models/organization-activity.model';

@Component({
  selector: 'app-maintenance-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './maintenance-dashboard.component.html',
  styleUrls: ['./maintenance-dashboard.component.scss']
})
export class MaintenanceDashboardComponent implements OnInit {
  private apiUrl = environment.apiUrl;

  // Technical support phone number
  technicalSupportPhone = environment.technicalSupportPhone;
  formattedSupportPhone = formatPhoneNumber(environment.technicalSupportPhone);

  // Embedded playlist
  playlistEmbedUrl: SafeResourceUrl;

  isLoading = signal(true);

  // Ticket data
  openTicketCount = signal(0);
  openTickets = signal<SupportTicket[]>([]);

  // Appointment data
  pendingAppointmentCount = signal(0);
  activeAppointments = signal<ServiceAppointment[]>([]);

  // Activity data
  activities = signal<OrganizationActivity[]>([]);

  // Set when a ticket/appointment fetch 403s — this dashboard itself stays
  // Core-reachable (ADN-2281, TG7), but the two Hub endpoints it reads from
  // are Pro-gated (ADN-2278, TG4), so a Core org's fetch fails here. Without
  // this, the catch blocks below silently swallow the error and the
  // empty-state copy ("All caught up!") reads as "you have zero", which
  // misrepresents "you're on Core" (ADN-2284, TG10).
  proFeaturesLocked = signal(false);

  private organizationId: string | null = null;

  constructor(
    private router: Router,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private ticketService: SupportTicketService,
    private appointmentService: ServiceAppointmentService,
    private organizationService: OrganizationService
  ) {
    this.playlistEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://www.youtube.com/embed/videoseries?list=PLOXbV3LMb3R7PfYt2LklHrYFJVumy0ZxT'
    );
  }

  async ngOnInit(): Promise<void> {
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    this.isLoading.set(true);
    try {
      const org = await this.organizationService.getMyOrganization();
      if (!org) {
        this.isLoading.set(false);
        return;
      }
      this.organizationId = org.id;

      await Promise.all([
        this.loadTicketData(),
        this.loadAppointmentData(),
        this.loadMaintenanceActivities()
      ]);
    } catch (error) {
      console.error('Error initializing maintenance dashboard:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  private async loadTicketData(): Promise<void> {
    if (!this.organizationId) return;
    try {
      // Load counts
      const counts = await this.ticketService.getOrganizationTicketCounts(this.organizationId);
      this.openTicketCount.set(counts.open + counts.in_progress);

      // Load open tickets (limit to 5 most recent)
      const response = await this.ticketService.getOrganizationTickets(
        this.organizationId,
        { limit: 5 }
      );
      // Filter to only open/in_progress tickets
      const openStatuses: SupportTicketStatus[] = ['open', 'in_progress'];
      const filtered = response.tickets.filter(t => openStatuses.includes(t.status));
      this.openTickets.set(filtered.slice(0, 5));
      // Reused component instance could have a stale locked flag from a
      // prior Core-org load (e.g. an org switch without a route change).
      this.proFeaturesLocked.set(false);
    } catch (error: any) {
      if (error?.status === 403) {
        // Expected outcome for a Core org — not an error worth logging.
        // Clear any previously loaded data too, so a reused component
        // instance (e.g. an org/tier switch without a route change) can't
        // keep showing a stale Pro-tier snapshot under the locked notice.
        this.proFeaturesLocked.set(true);
        this.openTicketCount.set(0);
        this.openTickets.set([]);
        return;
      }
      console.error('Error loading ticket data:', error);
    }
  }

  private async loadAppointmentData(): Promise<void> {
    if (!this.organizationId) return;
    try {
      // Load counts
      const counts = await this.appointmentService.getOrganizationAppointmentCounts(this.organizationId);
      this.pendingAppointmentCount.set(
        counts.pending + counts.requested + counts.scheduled + counts.confirmed + counts.in_progress
      );

      // Load active appointments (limit to 5 most recent)
      const response = await this.appointmentService.getOrganizationAppointments(
        this.organizationId,
        { limit: 5 }
      );
      // Filter to only active appointments (not completed or cancelled)
      const activeStatuses: ServiceAppointmentStatus[] = ['pending', 'requested', 'scheduled', 'confirmed', 'in_progress'];
      const filtered = response.appointments.filter(a => activeStatuses.includes(a.status));
      this.activeAppointments.set(filtered.slice(0, 5));
      // Reused component instance could have a stale locked flag from a
      // prior Core-org load (e.g. an org switch without a route change).
      this.proFeaturesLocked.set(false);
    } catch (error: any) {
      if (error?.status === 403) {
        // Expected outcome for a Core org — not an error worth logging.
        // Clear any previously loaded data too, same reasoning as
        // loadTicketData's 403 branch above.
        this.proFeaturesLocked.set(true);
        this.pendingAppointmentCount.set(0);
        this.activeAppointments.set([]);
        return;
      }
      console.error('Error loading appointment data:', error);
    }
  }

  private async loadMaintenanceActivities(): Promise<void> {
    if (!this.organizationId) return;
    try {
      const maintenanceEventTypes: EventType[] = [
        'ticket.created',
        'ticket.closed',
        'ticket.reopened',
        'ticket.reply',
        'appointment.requested',
        'appointment.scheduled',
        'appointment.cancelled',
        'appointment.completed'
      ];

      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: { activities: OrganizationActivity[] } }>(
          `${this.apiUrl}/organizations/${this.organizationId}/activities`,
          { params: { limit: '10', eventTypes: maintenanceEventTypes.join(',') } }
        )
      );

      if (response.success && response.data) {
        this.activities.set(response.data.activities);
      }
    } catch (error) {
      console.error('Error loading maintenance activities:', error);
    }
  }

  // Ticket helpers
  getTicketStatusBadgeClass(status: SupportTicketStatus): string {
    const statusInfo = SUPPORT_TICKET_STATUSES.find(s => s.value === status);
    if (!statusInfo) return 'bg-secondary';
    if (statusInfo.color === 'warning') return 'bg-warning text-dark';
    if (statusInfo.color === 'medium') return 'bg-secondary';
    return `bg-${statusInfo.color}`;
  }

  getTicketStatusLabel(status: SupportTicketStatus): string {
    const statusInfo = SUPPORT_TICKET_STATUSES.find(s => s.value === status);
    return statusInfo?.label || status;
  }

  // Appointment helpers
  getAppointmentStatusBadgeClass(status: ServiceAppointmentStatus): string {
    const statusInfo = SERVICE_APPOINTMENT_STATUSES.find(s => s.value === status);
    if (!statusInfo) return 'bg-secondary';
    if (statusInfo.color === 'warning') return 'bg-warning text-dark';
    return `bg-${statusInfo.color}`;
  }

  getAppointmentStatusLabel(status: ServiceAppointmentStatus): string {
    const statusInfo = SERVICE_APPOINTMENT_STATUSES.find(s => s.value === status);
    return statusInfo?.label || status;
  }

  // Activity helpers
  getActivityIconClass(activity: OrganizationActivity): string {
    return getActivityIcon(activity.eventType);
  }

  hasResourceLink(activity: OrganizationActivity): boolean {
    return !!activity.resourceType && !!activity.resourceId;
  }

  navigateToResource(activity: OrganizationActivity): void {
    const route = getResourceRoute(activity.resourceType, activity.resourceId);
    if (route) {
      this.router.navigate([route]);
    }
  }


  // Utility helpers
  getRelativeTime(createdAt: string): string {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) {
      return 'Just now';
    } else if (diffMin < 60) {
      return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    } else if (diffHour < 24) {
      return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    } else if (diffDay < 7) {
      return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  truncateText(text: string, maxLength: number = 50): string {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }
}
